import React, { useEffect, useState } from "react";
import { fetchDailyThroughput } from "../api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useTheme, CircularProgress, Box } from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useCustomerLocale } from "../context/CustomerSettingsContext";

const DailyThroughputChart = ({ date, workcellId, activeIndex, onHover, loading, onLoadingChange }) => {
  const [data, setData] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const locale = useCustomerLocale();

  const formatCount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return t("common.na");
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(num);
  };

  useEffect(() => {
    let mounted = true;
    setInternalLoading(true);
    const callParent = typeof onLoadingChange === 'function';
    if (callParent) onLoadingChange(true);
    fetchDailyThroughput(date, workcellId)
      .then((apiData) => {
        if (!mounted) return;
        // Create a full 24-hour dataset with default 0 values
        const fullDayData = Array.from({ length: 24 }, (_, i) => ({
          hour: i.toString().padStart(2, "0"), // "00" to "23"
          work_done: 0,
        }));

        // Merge API data into the full dataset
        apiData.forEach((entry) => {
          const index = fullDayData.findIndex((d) => d.hour === entry.hour);
          if (index !== -1) {
            fullDayData[index].work_done = entry.work_done; // Replace 0 with actual value
          }
        });

        setData(fullDayData);
      })
      .catch(() => {
        if (!mounted) return;
      })
      .finally(() => {
        if (!mounted) return;
        setInternalLoading(false);
        if (callParent) onLoadingChange(false);
      });
    return () => { mounted = false; };
  }, [date, workcellId, onLoadingChange]);

  const isLoading = typeof loading === 'boolean' ? loading : internalLoading;
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{ background: '#fff', border: '1px solid #ccc', padding: 8 }}>
          <div><b>{t("daily.plannings.throughputChart.tooltip.hour")}:</b> {label}</div>
          <div style={{ color: theme.palette.primary.main }}>
            {t("daily.plannings.throughputChart.tooltip.workDone")}: #{formatCount(payload[0].value)}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={data}
        syncId="workcellCharts"
      >
        <XAxis dataKey="hour" />
        <YAxis
          label={{
            value: t("daily.plannings.throughputChart.axisLabel"),
            angle: -90,
            position: "insideLeft",
            style: { textAnchor: "middle" }
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar
          dataKey="work_done"
          fill={theme.palette.primary.main}
          name={t("daily.plannings.throughputChart.workDone")}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

DailyThroughputChart.propTypes = {
  date: PropTypes.string.isRequired,
  workcellId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  activeIndex: PropTypes.number,
  onHover: PropTypes.func,
  loading: PropTypes.bool,
};

export default DailyThroughputChart;
